import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SectionTitle from '../typography/SectionTitle';
import StyledDropzone from './StyledDropzone';

/** Props for the generic file uploader / manager. */
interface FileUploaderProps {
  /** API service exposing attachment methods. */
  service: any;
  /** Entity primary key the file belongs to. */
  entityId: number;
  /** Key used by the backend to identify the attachment field. */
  attachmentName: string;
  /** Full file path or URL returned by the backend; `null` if absent. */
  filename: string | null;
  /** Optional callback fired after a successful upload. */
  onUploadSuccess?: any;
  /** If `true`, only viewing / downloading is allowed. */
  disableEdit?: boolean;
}

/**
 * Extracts the bare file name from a URL or a filesystem path.
 *
 * @param path - Full URL or path; `null` when no file is attached.
 * @returns The file name segment, or empty string when input is null/empty.
 */
export const extractFilename = (path: string | null): string => {
  if (!path) return '';

  try {
    const url = new URL(path);
    path = url.pathname;
  } catch {
    // Not a valid URL – use the original string.
  }

  return path.split('/').pop() || path;
};

/** Returns `true` only when a non-empty file path is provided. */
const isAttachmentPresent = (path: string | null): boolean =>
  typeof path === 'string' && path.trim().length > 0;

/**
 * Manages a single file attachment for an entity.
 *
 * - **No attachment** → shows an upload trigger that opens the drop-zone dialog.
 * - **Attachment present** → shows the file name; clicking it opens an action
 *   dialog where the user can download or delete the file (delete is hidden in
 *   read-only mode).
 */
const FileUploader = ({
  service,
  entityId,
  attachmentName,
  filename,
  onUploadSuccess,
  disableEdit = false
}: FileUploaderProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  /* ------------------ State ------------------ */

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [hasAttachment, setHasAttachment] = useState<boolean>(false);
  const [fileNameDisplay, setFileNameDisplay] = useState<string>(extractFilename(filename));

  /* ------------------ Effects ------------------ */

  /** Sync local state whenever the parent filename prop changes. */
  useEffect(() => {
    setHasAttachment(isAttachmentPresent(filename));
    setFileNameDisplay(extractFilename(filename));
  }, [filename]);

  /* ------------------ Upload dialog handlers ------------------ */

  const handleOpenUploadDialog = () => {
    if (entityId) setIsUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setSelectedFile(null);
    setIsUploadDialogOpen(false);
  };

  const handleFileDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      enqueueSnackbar(
        t('attachment-modal.file-too-large') || 'The file exceeds the maximum allowed size.',
        { variant: 'error' }
      );
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      setFileNameDisplay(file.name);
      setSelectedFile(file);
    }
  };

  const handleConfirmUpload = () => {
    if (selectedFile && entityId) {
      service.attachFiles(selectedFile, attachmentName, entityId).then(() => {
        if (onUploadSuccess) onUploadSuccess();
        setHasAttachment(true);
        enqueueSnackbar(t('attachment-modal.upload-success') || 'Attachment uploaded.', {
          variant: 'success'
        });
        setIsUploadDialogOpen(false);
      });
    }
  };

  /* ------------------ Action dialog handlers ------------------ */

  const handleOpenActionDialog = () => setIsActionDialogOpen(true);
  const handleCloseActionDialog = () => setIsActionDialogOpen(false);

  const handleDownload = () => {
    if (entityId) {
      service.downloadAttachment(entityId, attachmentName);
    }
    setIsActionDialogOpen(false);
  };

  /** Removes the current attachment and resets local UI state. */
  const handleDeleteAttachment = () => {
    if (entityId) {
      service.deleteAttachment(entityId, attachmentName);
      setHasAttachment(false);
      setFileNameDisplay(t('actions.insert'));
      enqueueSnackbar(t('attachment-modal.delete-success') || 'Attachment removed.', {
        variant: 'success'
      });
    }
    setIsActionDialogOpen(false);
  };

  /* ------------------ Render ------------------ */

  return (
    <Box id="file-uploader-box" sx={{ width: '100%' }}>
      {/* ----- Trigger area ----- */}
      <Box
        sx={{
          display: 'flex',
          minWidth: 0,
          justifyContent: 'end',
          alignItems: 'center'
        }}>
        {!hasAttachment ? (
          /* -- No file: upload trigger -- */
          disableEdit ? (
            <Typography variant="body2" color="text.secondary">
              {t('actions.insert')}
            </Typography>
          ) : (
            <Tooltip title={t('actions.upload-attachment')} enterDelay={200}>
              <Button
                startIcon={<UploadFileIcon fontSize="small" />}
                onClick={handleOpenUploadDialog}>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}>
                  {/* {t('actions.upload-attachment')} */} +
                </Box>
              </Button>
            </Tooltip>
          )
        ) : (
          /* -- File present: open action dialog -- */
          <Tooltip title={fileNameDisplay.toUpperCase()} enterDelay={200}>
            <Button
              startIcon={<InsertDriveFileIcon fontSize="small" />}
              onClick={handleOpenActionDialog}>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}>
                {/* {fileNameDisplay} */} 1
              </Box>
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* ===== Action dialog (download / delete) ===== */}
      <Dialog open={isActionDialogOpen} onClose={handleCloseActionDialog} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 2
          }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {t('actions.attachment-action-dialog.title', {
              filename: fileNameDisplay
            })}
          </Typography>
          <IconButton onClick={handleCloseActionDialog} aria-label={t('actions.close') || 'Close'}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('actions.attachment-action-dialog.description')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider'
            }}>
            <InsertDriveFileIcon color="action" />
            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
              {fileNameDisplay}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {!disableEdit && (
            <Button variant="outlined" color="error" onClick={handleDeleteAttachment}>
              {t('actions.delete') || 'Elimina'}
            </Button>
          )}
          <Button variant="contained" onClick={handleDownload} startIcon={<DownloadIcon />}>
            {t('actions.download') || 'Scarica'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Upload dialog (drop-zone) ===== */}
      <Dialog open={isUploadDialogOpen} onClose={handleCloseUploadDialog} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 2
          }}>
          <SectionTitle>{t('attachment-modal.title')}</SectionTitle>
          <IconButton onClick={handleCloseUploadDialog} aria-label={t('actions.close') || 'Close'}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          <StyledDropzone onDrop={handleFileDrop} />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button variant="outlined" onClick={handleCloseUploadDialog}>
            {t('actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleConfirmUpload} disabled={!selectedFile}>
            {t('actions.upload-attachment')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileUploader;
